<template>
    <Teleport to="body">
        <Transition name="modal-fade">
            <div
                v-if="isVisible"
                class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-[2px]"
                @click.self="closeModal"
            >
                <div
                    class="w-full max-w-md md:max-w-lg bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-gray-100 overflow-hidden"
                    role="dialog"
                    aria-modal="true"
                    :aria-labelledby="state === 'email_input' ? 'change-email-title' : 'confirm-email-title'"
                >
                    <div class="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
                        <div class="flex-1 pr-4">
                            <div class="flex items-center gap-3 mb-4">
                                <div
                                    class="flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-colors"
                                    :class="state === 'email_input' ? 'bg-blue-600 text-white' : 'bg-emerald-50 text-emerald-700'"
                                >
                                    1
                                </div>
                                <div class="flex-1 h-px bg-gray-200" />
                                <div
                                    class="flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-colors"
                                    :class="state === 'code_input' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-400'"
                                >
                                    2
                                </div>
                            </div>

                            <h2
                                v-if="state === 'email_input'"
                                id="change-email-title"
                                class="text-lg font-semibold text-gray-900"
                            >
                                Изменить email
                            </h2>
                            <h2
                                v-else
                                id="confirm-email-title"
                                class="text-lg font-semibold text-gray-900"
                            >
                                Подтвердите email
                            </h2>

                            <p class="text-sm text-gray-500 mt-1">
                                <template v-if="state === 'email_input'">
                                    Укажите новый адрес — на текущую почту придёт код подтверждения.
                                </template>
                                <template v-else>
                                    Введите код из письма, отправленного на
                                    <span class="font-medium text-gray-700">{{ oldUserEmail }}</span>
                                </template>
                            </p>
                        </div>

                        <button
                            type="button"
                            class="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                            aria-label="Закрыть"
                            @click="closeModal"
                        >
                            <svg class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                            </svg>
                        </button>
                    </div>

                    <div class="px-6 py-5">
                        <div v-if="state === 'email_input'" class="space-y-4">
                            <div>
                                <label for="new-email" class="block text-sm font-medium text-gray-700 mb-1.5">
                                    Новый email
                                </label>
                                <input
                                    id="new-email"
                                    v-model="email"
                                    type="email"
                                    autocomplete="email"
                                    placeholder="name@example.com"
                                    class="w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-200 rounded-lg placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                />
                            </div>

                            <div>
                                <label for="repeat-email" class="block text-sm font-medium text-gray-700 mb-1.5">
                                    Повторите email
                                </label>
                                <input
                                    id="repeat-email"
                                    v-model="emailRepeat"
                                    type="email"
                                    autocomplete="email"
                                    placeholder="name@example.com"
                                    class="w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-200 rounded-lg placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                />
                            </div>
                        </div>

                        <div v-else class="space-y-4">
                            <div class="rounded-lg bg-blue-50 border border-blue-100 px-4 py-3">
                                <p class="text-sm text-blue-800">
                                    Код подтверждения отправлен. Проверьте папку «Входящие» и «Спам».
                                </p>
                            </div>

                            <div>
                                <label for="confirm-code" class="block text-sm font-medium text-gray-700 mb-1.5">
                                    Код подтверждения
                                </label>
                                <input
                                    id="confirm-code"
                                    v-model="code"
                                    type="text"
                                    inputmode="numeric"
                                    autocomplete="one-time-code"
                                    placeholder="Введите код"
                                    class="w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-200 rounded-lg placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors tracking-widest"
                                />
                            </div>

                            <div class="flex items-center gap-1 text-sm text-gray-500">
                                <span>Не пришёл код?</span>
                                <button
                                    v-if="isSendButtonActive"
                                    type="button"
                                    class="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                                    @click="resendCode"
                                >
                                    Отправить ещё раз
                                </button>
                                <span v-else class="font-medium text-gray-400">
                                    Повторная отправка через 0:{{ formattedTimer }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
                        <button
                            type="button"
                            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            @click="closeModal"
                        >
                            Отмена
                        </button>

                        <button
                            v-if="state === 'email_input'"
                            type="button"
                            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                            @click="sendRequestOnEmailChange"
                        >
                            Отправить код
                        </button>
                        <button
                            v-else
                            type="button"
                            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                            @click="submitCode"
                        >
                            Подтвердить
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { createToaster } from "@meforma/vue-toaster";
import { useRouter } from "vue-router";
import { computed, defineModel, onUnmounted, ref, watch } from 'vue';
import { Api } from "@/service/api";

const toaster = createToaster();
const router = useRouter();
const isVisible = defineModel();

const email = ref("");
const emailRepeat = ref("");
const oldUserEmail = ref("");
const code = ref("");
const state = ref("email_input");
const currentTime = ref(0);
const isSendButtonActive = ref(true);

let timer = null;

const formattedTimer = computed(() => {
    return currentTime.value >= 10 ? String(currentTime.value) : `0${currentTime.value}`;
});

watch(currentTime, (time) => {
    if (time === 0) {
        stopTimer();
    }
});

watch(isVisible, (visible) => {
    if (!visible) {
        resetForm();
    }
});

onUnmounted(() => stopTimer());

function resetForm() {
    email.value = "";
    emailRepeat.value = "";
    oldUserEmail.value = "";
    code.value = "";
    state.value = "email_input";
    stopTimer();
}

function closeModal() {
    isVisible.value = false;
}

async function sendRequestOnEmailChange() {
    if (email.value === "" || emailRepeat.value === "") {
        toaster.error("Введите почту");
        return;
    }
    if (email.value !== emailRepeat.value) {
        toaster.error("Адреса не совпадают");
        return;
    }

    const response = await Api.sendRequestOnEmailChange(email.value);

    if (response.status === "error") {
        toaster.error(response.message);
        return;
    }

    if (response.status === "success") {
        state.value = "code_input";
        oldUserEmail.value = response.data;
    }
}

function startTimer(time) {
    currentTime.value = time;
    timer = setInterval(() => {
        currentTime.value--;
    }, 1000);
    isSendButtonActive.value = false;
}

function stopTimer() {
    clearInterval(timer);
    timer = null;
    isSendButtonActive.value = true;
}

async function resendCode() {
    const request = await Api.resendEmailRequestCode(email.value);

    if (request.status === "error") {
        toaster.error(request.message);
        return;
    }

    startTimer(59);
}

async function submitCode() {
    if (email.value === "" || emailRepeat.value === "") {
        toaster.error("Введите почту");
        return;
    }
    if (email.value !== emailRepeat.value) {
        toaster.error("Адреса не совпадают");
        return;
    }
    if (code.value === "") {
        toaster.error("Введите код");
        return;
    }

    const request = await Api.changeEmail(email.value, code.value);

    if (request.status === "error") {
        toaster.error(request.message);
        return;
    }

    if (request.status === "success") {
        stopTimer();
        closeModal();
        router.go(0);
    }
}
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
}
</style>
